import React, { useCallback, useEffect, useState } from 'react';
import { adminService, staffService } from '../../services';
import { useUser } from '../../context/UserContext';
// Leaves only, never the `./shell` barrel — PersonDrawer mounts this and AdminShell mounts
// PersonDrawer, so the barrel would be a cycle.
import { Btn, Loading, SubTabs, money, shortDate, signedMoney } from './shell/ui';
import DataTable from './shell/DataTable';
import WalletMoveModal from './WalletMoveModal';
import { activityColumns } from './activityColumns';

/**
 * A person's full record: the wallet strip (BALANCE · ESCROW · EARNED · SPENT · FLAKE FEES),
 * ADJUST WALLET / TRANSFER WALLET (the shared `WalletMoveModal`), and the five tabs — TRANSACTIONS · EVENTS HOSTED · APPLICATIONS · WITHDRAWALS
 * · STAFF ACTIVITY ON THIS USER. One component, two homes: the USERS detail page and the
 * person drawer once FULL RECORD is pressed (Oct 26 2026). `GET api/admin/users/{id}` feeds it.
 */
export default function UserRecord({ userId, onChanged, className = '' }) {
  const { can, isSuperadmin } = useUser();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('transactions');
  const [walletMode, setWalletMode] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await adminService.getUserDetail(userId);
    if (r.success) setData(r.data); else setData(null);
    setLoading(false);
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className={`p-6 ${className}`}><Loading /></div>;
  if (!data) return <div className={`p-6 font-mono text-[11px] text-[#DC2626] ${className}`}>COULD NOT LOAD THIS RECORD.</div>;
  const { user: u, wallet, transactions = [], eventsHosted = [], applications = [], withdrawals = [] } = data;

  const tabs = [
    { key: 'transactions', label: 'TRANSACTIONS' }, { key: 'events', label: 'EVENTS HOSTED' },
    { key: 'applications', label: 'APPLICATIONS' }, { key: 'withdrawals', label: 'WITHDRAWALS' },
    can('activity', 'view') && { key: 'activity', label: 'STAFF ACTIVITY ON THIS USER' },
  ].filter(Boolean);

  return (
    <div className={className} data-testid="user-record">
      <div className="border-t border-[#F2F2F2] px-5 py-4">
        <div className="flex flex-wrap items-center gap-6">
          {wallet ? (
            <>
              {[['BALANCE', money(wallet.balance), 'font-bold'], ['ESCROW', money(wallet.escrowBalance), ''], ['EARNED', money(wallet.totalEarned), 'text-[#15803D]'], ['SPENT', money(wallet.totalSpent), ''], ['FLAKE FEES', money(wallet.totalFlakeFees), 'text-[#DC2626]']].map(([l, v, cls]) => (
                <div key={l}><div className="font-mono text-[10px] font-bold uppercase tracking-[0.1em] text-[#6B7280]">{l}</div><div className={`font-mono text-[13px] ${cls}`}>{v}</div></div>
              ))}
            </>
          ) : <span className="font-mono text-[11px] font-bold uppercase tracking-[0.1em] text-[#6B7280]">NO WALLET.</span>}
          {(can('wallets', 'adjust') || isSuperadmin) && (
            <span className="ml-auto flex gap-2">
              {can('wallets', 'adjust') && <Btn onClick={() => setWalletMode('adjust')} className="px-[11px] py-[7px]">ADJUST WALLET</Btn>}
              {isSuperadmin && <Btn onClick={() => setWalletMode('transfer')} className="px-[11px] py-[7px]">TRANSFER WALLET</Btn>}
            </span>
          )}
        </div>
        <WalletMoveModal
          person={walletMode ? { id: userId, name: u.displayName || u.email, email: u.email, handle: u.handle, balance: wallet?.balance ?? 0, escrow: wallet?.escrowBalance ?? 0 } : null}
          initialMode={walletMode || 'adjust'}
          onClose={() => setWalletMode(null)}
          onDone={() => { load(); onChanged?.(); }}
        />
      </div>

      <div className="px-5 pb-1 pt-2"><SubTabs tabs={tabs} active={tab} onChange={setTab} /></div>
      <div className="px-2 pb-3">
        {tab === 'transactions' && (
          <DataTable
            columns={[
              { key: 'createdAt', label: 'DATE', fr: 0.8, render: (t) => shortDate(t.createdAt) },
              { key: 'type', label: 'TYPE', fr: 1.4 },
              { key: 'amount', label: 'AMOUNT', fr: 0.9, right: true, render: (t) => signedMoney(t.amount) },
              { key: 'balanceAfter', label: 'BALANCE', fr: 0.9, right: true, render: (t) => money(t.balanceAfter) },
              { key: 'eventTitle', label: 'EVENT', fr: 1.4, render: (t) => t.eventTitle || '—' },
              { key: 'description', label: 'DESCRIPTION', fr: 1.6, render: (t) => t.description || '—' },
            ]}
            rows={transactions}
            rowKey={(t) => t.id}
          />
        )}
        {tab === 'events' && (
          <DataTable
            columns={[
              { key: 'title', label: 'TITLE', fr: 2 },
              { key: 'status', label: 'STATUS', fr: 1, render: (e) => String(e.status).replace('_', ' ').toUpperCase() },
              { key: 'dateTime', label: 'DATE', fr: 1, render: (e) => (e.dateTime ? shortDate(e.dateTime) : '—') },
              { key: 'fundingGoalCents', label: 'GOAL', fr: 1, right: true, render: (e) => money(e.fundingGoalCents) },
              { key: 'applicationCount', label: 'GUESTS', fr: 1.2, right: true, render: (e) => `${e.applicationCount} (${e.acceptedCount} accepted)` },
            ]}
            rows={eventsHosted}
            rowKey={(e) => e.id}
          />
        )}
        {tab === 'applications' && (
          <DataTable
            columns={[
              { key: 'eventTitle', label: 'EVENT', fr: 2 },
              { key: 'status', label: 'STATUS', fr: 1, render: (a) => String(a.status).toUpperCase() },
              { key: 'partyCreditsCommitted', label: 'COMMITTED', fr: 1, right: true, render: (a) => `${money(a.partyCreditsCommitted)}${a.partyCreditsRefunded ? ' (refunded)' : ''}` },
              { key: 'attendanceStatus', label: 'ATTENDANCE', fr: 1, render: (a) => a.attendanceStatus || '—' },
              { key: 'createdAt', label: 'APPLIED', fr: 1, render: (a) => shortDate(a.createdAt) },
            ]}
            rows={applications}
            rowKey={(a) => a.id}
          />
        )}
        {tab === 'withdrawals' && (
          <DataTable
            columns={[
              { key: 'amountCents', label: 'AMOUNT', fr: 1, right: true, render: (w) => money(w.amountCents) },
              { key: 'feeCents', label: 'FEE', fr: 1, right: true, render: (w) => money(w.feeCents) },
              { key: 'netAmountCents', label: 'NET', fr: 1, right: true, render: (w) => money(w.netAmountCents) },
              { key: 'status', label: 'STATUS', fr: 1, render: (w) => String(w.status).toUpperCase() },
              { key: 'createdAt', label: 'DATE', fr: 1, render: (w) => shortDate(w.createdAt) },
            ]}
            rows={withdrawals}
            rowKey={(w) => w.id}
          />
        )}
        {tab === 'activity' && <StaffActivityOnUser userId={userId} />}
      </div>
    </div>
  );
}

export function StaffActivityOnUser({ userId }) {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  useEffect(() => { (async () => { const r = await staffService.activity({ target: `user:${userId}`, page, pageSize: 25 }); setData(r.success ? r.data : { rows: [], total: 0 }); })(); }, [userId, page]);
  return (
    <DataTable
      columns={activityColumns({ withSection: true })}
      rows={data?.rows || []}
      loading={!data}
      rowKey={(r) => r.id}
      empty="NOBODY ON STAFF HAS TOUCHED THIS RECORD."
      total={data?.total ?? 0}
      page={page}
      pageSize={25}
      onPage={setPage}
    />
  );
}
